/**
 * SSO Callback Page
 * Handles the redirect from the backend after Azure AD authentication.
 *
 * SECURITY (#1131): the backend no longer puts session/refresh tokens in the
 * redirect URL (they leak via logs, browser history, and Referer headers).
 * Instead it returns a short-lived, single-use opaque `code`, which this page
 * redeems over POST /auth/sso/exchange to receive the tokens in the response
 * body. The tokens never touch the URL.
 */

import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { setAuthTokens } from '@/utils/auth-storage'
import { API_URL } from '@/lib/env'
import { Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import logoImage from '@/assets/images/logo.png'
import logoImageDark from '@/assets/images/logo-dark.png'

export function SsoCallbackPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const exchangeCode = searchParams.get('code')
    const ssoError = searchParams.get('sso_error')

    if (ssoError) {
      setError(decodeURIComponent(ssoError))
      return
    }

    if (!exchangeCode) {
      setError('Missing exchange code')
      return
    }

    let cancelled = false

    const redeemCode = async () => {
      try {
        const response = await fetch(`${API_URL.replace(/\/$/, '')}/auth/sso/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: exchangeCode }),
        })

        const payload = await response.json().catch(() => null)

        if (!response.ok || !payload?.success) {
          throw new Error(payload?.error?.message || 'Sign-in failed')
        }

        const session = payload.data?.session
        if (!session?.sessionToken) {
          throw new Error('Missing session token')
        }

        if (cancelled) return

        setAuthTokens({
          accessToken: session.sessionToken,
          refreshToken: session.refreshToken || undefined,
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
          refreshExpiresAt: session.refreshToken
            ? Date.now() + 15 * 24 * 60 * 60 * 1000
            : undefined,
        })

        // Honor a post-login destination (e.g. the agent chat on an agent
        // custom domain). Only accept same-origin relative paths to avoid open
        // redirects.
        const next = searchParams.get('next')
        const dest =
          next && next.startsWith('/') && !next.startsWith('//') ? next : '/app'
        navigate(dest, { replace: true })
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Sign-in failed')
      }
    }

    void redeemCode()

    return () => {
      cancelled = true
    }
  }, [searchParams, navigate])

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 lg:w-96 lg:h-96 bg-primary-100 dark:bg-primary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
        <div className="absolute top-40 right-10 w-72 h-72 lg:w-96 lg:h-96 bg-secondary-100 dark:bg-secondary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-card shadow-xl rounded-2xl border border-border p-8 text-center">
            <div className="flex justify-center mb-6">
              <img src={logoImage} alt="Logo" className="h-10 w-auto block dark:hidden" />
              <img src={logoImageDark} alt="Logo" className="h-10 w-auto hidden dark:block" />
            </div>

            {error ? (
              <div className="space-y-4">
                <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
                <h2 className="text-lg font-semibold">Sign-in failed</h2>
                <p className="text-sm text-muted-foreground">{error}</p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/signin', { replace: true })}
                  className="mt-4"
                >
                  Back to Sign in
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
                <h2 className="text-lg font-semibold">Completing sign-in...</h2>
                <p className="text-sm text-muted-foreground">
                  Please wait while we verify your Microsoft account.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
